import Chip from '@/components/elements/Chip';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import Icon from '@/components/icons/Icon';
import { APP_ROUTES } from '@/constants/pages';
import { classnames, getGeoLocation } from '@/helpers';
import { SearchManagerOutput } from '@/hooks/search/useSearch';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
} from '@/hooks/search/useSearch.constants';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { SortOption } from '@/types/state/search';
import { Fragment, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Filter from '../components/Filter/Filter';
import { getSelectedFilters } from '../components/Filter/Filter.helpers';
import useFilterManager from '../components/Filter/Filter.hooks';
import { FilterOption } from '../components/Filter/Filter.types';
import Sorting from '../components/Sorting/Sorting';
import useSortingManager from '../components/Sorting/Sorting.hooks';

type SearchControlProps = SearchManagerOutput & {
  showSortingToggle?: boolean;
  showFilterToggle?: boolean;
  showSistaminutenToggle?: boolean;
};

const baseTranslationKey = 'features.searchV2.modules.SearchControls';

const SearchControls = ({
  showFilterToggle = true,
  showSortingToggle = true,
  showSistaminutenToggle = true,
  ...props
}: SearchControlProps) => {
  const { isMobileView } = useMobileView();
  const history = useHistory();
  const selectionOptions = getSelectedFilters(props.prefs);
  const [isLoadingGeoLocation, setIsLoadingGeoLocation] = useState<boolean>(false);
  const isSistaminutenSearch = props.type === 'sistaminuten';

  const filterManager = useFilterManager({
    initialState: { prefs: props.prefs, showOptions: false },
    urlPrefs: props.prefs,
    applyOnToggle: !isMobileView,
    onChangeAppliedFilters: handleOnChangeAppliedFilters,
  });

  const sortingManager = useSortingManager({
    initialState: { sort: props.sort, showOptions: false },
    urlSorting: props.sort,
    onChangeSorting: handleOnChangeSorting,
    onClearSorting: handleClearSorting,
  });

  function handleClearSorting() {
    /**
     * If user clears sorting by distance (closest), we need to clear the lat and lon
     * in all cases where the user is not doing a "near me" location search
     */
    if (props.location !== DEFAULT_MY_LOCATION_KEYWORD) {
      props.performSearch({ sort: null, lat: undefined, lon: undefined });
      return;
    }

    props.performSearch({ sort: null });
  }

  function handleOnChangeSorting(sort: SortOption) {
    /**
     *  if we are sorting by closest, we need to clear the location since we are not using it
     *  instead we want to sort by the closest location to the user position
     */
    if (sort === 'closest') {
      getGeoLocation(
        // on success handler
        (lat: string, lon: string) => {
          props.performSearch({ sort, lat: +lat, lon: +lon });
          setIsLoadingGeoLocation(false);
        },
        () => {},
        // on loading handler
        () => {
          setIsLoadingGeoLocation(true);
        },
        // on error handler
        (error) => {
          setIsLoadingGeoLocation(false);
          toast(
            ({ closeToast }) => (
              <Snackbar onClose={closeToast} label={_s(`${baseTranslationKey}.snackbar.error.userLocationDenied`)} />
            ),
            { autoClose: false },
          );
        },
      );
    } else {
      const keepCoordinates = props.location === DEFAULT_MY_LOCATION_KEYWORD;
      props.performSearch({ sort, ...(!keepCoordinates && { lat: undefined, lon: undefined }) });
    }
  }

  function handleOnChangeAppliedFilters(prefs: string) {
    props.performSearch({ prefs });
  }

  function handleOnRemoveSingleFilter(filterId: FilterOption['id']) {
    return (event) => {
      event?.stopPropagation();
      event?.preventDefault();
      const selectedOptions = getSelectedFilters(props.prefs);
      const newOptions = selectedOptions.filter((option) => option.id !== filterId).map((option) => option.id);
      props.performSearch({ prefs: newOptions.join(',') });
    };
  }

  const sistaminutenToggle = (
    <button
      onClick={() => {
        const keyword = props.q || DEFAULT_SEARCH_KEYWORD;
        const location = props.location || DEFAULT_SEARCH_LOCATION;
        let to = `/${keyword}/${location}`;

        if (!isSistaminutenSearch) {
          to = APP_ROUTES.SISTA_MINUTEN_SERP + to;
        }

        history.push(to);
      }}
      className={classnames(
        'text-m border-border_regular px-xs relative flex h-[32px] items-center justify-center rounded-lg border outline-none',
        'space-x-xxs relative flex items-center rounded-full border',
        isSistaminutenSearch ? 'bg-primary border-border_selected' : 'bg-surface_l2 border-border_regular',
      )}>
      <Icon variant="smt-filled" color="bg_accent_5" size="xs" />
      <span className={classnames(isSistaminutenSearch ? 'text-fg_primary_inverse' : 'text-fg_primary')}>
        Sista Minuten
      </span>
      {isSistaminutenSearch && (
        <button>
          <Icon variant="close" color="fg_primary_inverse" />
        </button>
      )}
    </button>
  );

  return (
    <Fragment>
      <div className="gap-sm flex flex-col items-baseline">
        <ul className="space-x-sm !relative z-10 flex">
          {showSortingToggle && (
            <li className="shrink-0">
              <Sorting {...sortingManager} loading={isLoadingGeoLocation} isSistaminutenSearch={isSistaminutenSearch} />
            </li>
          )}
          {showFilterToggle && (
            <li className="shrink-0">
              <Filter {...filterManager} isSistaminutenSearch={isSistaminutenSearch} />
            </li>
          )}
          {!isMobileView && showSistaminutenToggle && <li className="shrink-0">{sistaminutenToggle}</li>}
          {!isMobileView && selectionOptions.length > 0 && (
            <li>
              <ul className="gap-sm no-scrollbar flex overflow-x-scroll rounded-sm">
                {selectionOptions.map((selectedOption) => (
                  <li key={selectedOption.id} className="flex-shrink-0">
                    <Chip label={selectedOption.label} onClose={handleOnRemoveSingleFilter(selectedOption.id)} />
                  </li>
                ))}
              </ul>
            </li>
          )}
        </ul>
        {isMobileView && showSistaminutenToggle && sistaminutenToggle}
      </div>
    </Fragment>
  );
};

export default SearchControls;
